import { Theme, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { MouseEvent, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../app/hooks';
import { RootState } from '../../app/store';
import { selectSelectedItemCartId } from '../../selectors/dialog';
import { CartItem } from '../../utils/cart';
import Colors from '../../utils/color';
import { convertModGroupToMap, sortModGroups } from '../../utils/menu';
import CartItemChildModGroup from './CartItemChildModGroup';
import { dialogActions } from '../../reducers/dialogSlice';
import { convertToMap } from '../../utils';
import { ParsedModifierGroup } from '../../types/menu';

interface ICartItemModGroups {
  cartItem: CartItem;
  modGroupCartItem: CartItem;
  quantity: number;
  selectable?: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  cartItemModGroupClass: {
    paddingLeft: theme.spacing(1.75),
  },
  modGroupErrorClass: {
    fontSize: `${theme.spacing(1.5)} !important`,
  },
  selectedModGroupClass: {
    backgroundColor: Colors.alto,
  },
}));

export default function CartItemModGroups({
  modGroupCartItem,
  cartItem,
  quantity,
  selectable,
}: ICartItemModGroups) {
  const dispatch = useAppDispatch();
  const { selectedModGroupPath } = useSelector(
    (state: RootState) => state.dialog
  );
  const selectedItemCartId = useSelector(selectSelectedItemCartId);
  const { cartItemModGroupClass, modGroupErrorClass, selectedModGroupClass } =
    useStyles();
  const childModifierGroups = convertModGroupToMap(
    Object.values({
      ...modGroupCartItem.childModifierGroups,
    }).filter((modGroup) => Object.keys(modGroup.selectedItems).length)
  );

  const childModifierGroupIDs = Object.keys(childModifierGroups);

  const requiredButNotSelectedModifierGroups = convertToMap(
    Object.values(modGroupCartItem.modifierGroups).filter(
      (modGroup) =>
        modGroup.minimumSelections > 0 &&
        !childModifierGroupIDs.includes(modGroup.id)
    )
  );
  const requiredButNotSelectedModifierGroupIDs = Object.keys(
    requiredButNotSelectedModifierGroups
  );

  const isModifierGroupSelected = (modGroup: ParsedModifierGroup): boolean => {
    if (selectedItemCartId !== cartItem.cartItemId) return false;

    const isNestedContext = cartItem.cartItemId !== modGroupCartItem.cartItemId;

    if (isNestedContext && selectedModGroupPath) {
      const parentModGroupId = modGroupCartItem.parentModifierGroupId;
      if (parentModGroupId) {
        const constructedPath = `${parentModGroupId}__${modGroupCartItem.id}__${modGroup.id}`;
        return (
          selectedModGroupPath === constructedPath ||
          selectedModGroupPath.endsWith(`__${modGroup.id}`)
        );
      }
    }

    return selectedModGroupPath === modGroup.id;
  };

  const sortedModGroups = useMemo(
    () => sortModGroups(modGroupCartItem),
    [modGroupCartItem]
  );
  const selectModGroupToFocus = (
    e: MouseEvent,
    modGroup: ParsedModifierGroup
  ) => {
    if (!selectable) return;

    e.stopPropagation();

    const isNestedContext = cartItem.cartItemId !== modGroupCartItem.cartItemId;

    // Reset navigation and select item
    dispatch(
      dialogActions.selectItemAndResetNavigation({
        item: cartItem,
        itemCartId: cartItem.cartItemId,
      })
    );

    // Determine pathToItem based on context and available data
    const pathToItem = getModifierGroupPath(modGroup, isNestedContext);

    if (isNestedContext) {
      const parentModGroupId = modGroupCartItem.parentModifierGroupId;
      if (parentModGroupId) {
        // Set navigation to nested level
        dispatch(
          dialogActions.navigateToNestedLevel({
            parentModGroupId,
            firstNestedModGroup: modGroup,
            firstNestedModGroupPath: pathToItem,
          })
        );
      }
    }

    // Update selected modifier group and index
    dispatch(
      dialogActions.updateSelectedModGroup({
        modGroup,
        pathToItem,
      })
    );

    const modGroupIndex = sortedModGroups.findIndex(
      (mg) => mg.id === modGroup.id
    );
    if (modGroupIndex !== -1) {
      dispatch(dialogActions.setSelectedModGroupIndex(modGroupIndex + 1));
    }
  };

  const getModifierGroupPath = (
    modGroup: ParsedModifierGroup,
    isNestedContext: boolean
  ): string => {
    const cartModGroup = modGroup as any;

    // Check for existing pathToItem in selected items (BM case)
    if (cartModGroup.selectedItems) {
      const selectedItemsWithPathToItem = Object.values(
        cartModGroup.selectedItems
      ).filter((item: any) => item.pathToItem);

      if (selectedItemsWithPathToItem.length > 0) {
        return (selectedItemsWithPathToItem[0] as any).pathToItem;
      }
    }

    // Fallback: construct path based on context
    if (isNestedContext) {
      const parentModGroupId = modGroupCartItem.parentModifierGroupId;
      return `${parentModGroupId}__${modGroupCartItem.id}__${modGroup.id}`;
    }

    return modGroup.id;
  };

  return (
    <div className={`cartitem-mod-group ${cartItemModGroupClass}`}>
      {sortModGroups(modGroupCartItem).map((modGroup) => {
        const isSelected = isModifierGroupSelected(modGroup);
        if (childModifierGroupIDs.includes(modGroup.id))
          return (
            <CartItemChildModGroup
              key={modGroup.id}
              quantity={quantity}
              cartItem={cartItem}
              modGroup={childModifierGroups[modGroup.id]}
              isSelected={isSelected}
              selectModGroupToFocus={selectModGroupToFocus}
            />
          );
        if (requiredButNotSelectedModifierGroupIDs.includes(modGroup.id))
          return (
            <div
              key={modGroup.id}
              onClick={(e) => selectModGroupToFocus(e, modGroup)}
              className={isSelected ? selectedModGroupClass : undefined}
            >
              <Typography
                className={modGroupErrorClass}
                color={Colors.Thunderbird}
                variant="subtitle2"
                children={'! ' + modGroup.name}
              />
            </div>
          );
        return null;
      })}
    </div>
  );
}
